import React, { useEffect, useMemo, useState } from 'react';
import { NavLink, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import {
  Sheet, SheetContent, SheetTrigger,
} from '@/components/ui/sheet';
import {
  LayoutDashboard, Building2, Users, ClipboardList, FileText, GraduationCap, DoorClosed,
  Wallet, BarChart3, Shield, LogOut, ChevronDown, Menu, Bell, User, KeyRound, Search, PlusCircle,
  Briefcase, CalendarDays, ArrowRightLeft,
} from 'lucide-react';

const mainNav = [
  { to: '/dashboard', label: 'Dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { to: '/companies', label: 'Companies', icon: Building2, testId: 'nav-companies' },
  { to: '/calendar', label: 'Calendar', icon: CalendarDays, testId: 'nav-calendar' },
  { to: '/analytics', label: 'Analytics', icon: BarChart3, testId: 'nav-analytics' },
];

const companyNav = (cid) => [
  { to: `/companies/${cid}/jobs`, label: 'Job Requirements', icon: Briefcase, testId: 'nav-jobs' },
  { to: `/companies/${cid}/recruitment`, label: 'Recruitment', icon: Users, testId: 'nav-recruitment' },
  { to: `/companies/${cid}/onboarding`, label: 'Onboarding', icon: ClipboardList, testId: 'nav-onboarding' },
  { to: `/companies/${cid}/policies`, label: 'HR Policies', icon: FileText, testId: 'nav-policies' },
  { to: `/companies/${cid}/training`, label: 'Training', icon: GraduationCap, testId: 'nav-training' },
  { to: `/companies/${cid}/offboarding`, label: 'Offboarding', icon: DoorClosed, testId: 'nav-offboarding' },
  { to: `/companies/${cid}/payroll`, label: 'Payroll', icon: Wallet, testId: 'nav-payroll' },
  { to: `/companies/${cid}/settings`, label: 'Settings', icon: KeyRound, testId: 'nav-company-settings' },
];

function useCompanies() {
  const [companies, setCompanies] = useState([]);
  useEffect(() => {
    api.get('/companies').then((r) => setCompanies(r.data)).catch(() => {});
  }, []);
  return [companies, setCompanies];
}

export function CompanySwitcher({ companies, activeId, onCreate }) {
  const navigate = useNavigate();
  const active = companies.find((c) => c.id === activeId);
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="w-full flex items-center gap-2 px-3 py-2 rounded-lg border bg-card hover:bg-accent transition-colors text-left"
          data-testid="company-switcher"
        >
          <div className="h-7 w-7 rounded-md bg-primary/10 text-primary flex items-center justify-center font-medium text-xs">
            {(active?.name || 'AL')[0]}
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium truncate">{active?.name || 'All companies'}</p>
            <p className="text-xs text-muted-foreground truncate">{active ? active.industry || 'Client company' : `${companies.length} clients`}</p>
          </div>
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-72">
        <DropdownMenuLabel>Switch company</DropdownMenuLabel>
        <DropdownMenuItem onClick={() => navigate('/companies')} data-testid="switcher-all">
          <Building2 className="h-4 w-4 mr-2" /> All companies
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <div className="max-h-64 overflow-y-auto">
          {companies.map((c) => (
            <DropdownMenuItem
              key={c.id}
              onClick={() => navigate(`/companies/${c.id}/recruitment`)}
              data-testid={`switcher-company-${c.id}`}
            >
              <div className="h-6 w-6 rounded bg-primary/10 text-primary text-[10px] flex items-center justify-center mr-2">
                {c.name[0]}
              </div>
              <span className="truncate">{c.name}</span>
              <span className="ml-auto text-xs text-muted-foreground">{c.candidate_count || 0}</span>
            </DropdownMenuItem>
          ))}
        </div>
        <DropdownMenuSeparator />
        <DropdownMenuItem onClick={onCreate} data-testid="switcher-create">
          <PlusCircle className="h-4 w-4 mr-2" /> Add new company
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export default function AppShell({ children }) {
  const { user, logout } = useAuth();
  const [companies] = useCompanies();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const activeCompanyId = params?.companyId || null;
  const [mobileOpen, setMobileOpen] = useState(false);

  const isAdmin = user?.role === 'admin';

  const NavList = () => (
    <div className="flex flex-col gap-1">
      <div className="px-2 pb-2">
        <CompanySwitcher
          companies={companies}
          activeId={activeCompanyId}
          onCreate={() => navigate('/companies?new=1')}
        />
      </div>
      <p className="px-4 pt-2 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Global</p>
      {mainNav.map((n) => (
        <NavLink
          key={n.to}
          to={n.to}
          data-testid={n.testId}
          className={({ isActive }) => cn(
            'mx-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors',
            isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground/70 hover:bg-accent hover:text-foreground'
          )}
        >
          <n.icon className="h-4 w-4" /> {n.label}
        </NavLink>
      ))}

      {activeCompanyId && (
        <>
          <p className="px-4 pt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Company workspace</p>
          {companyNav(activeCompanyId).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={n.testId}
              className={({ isActive }) => cn(
                'mx-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors',
                isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground/70 hover:bg-accent hover:text-foreground'
              )}
            >
              <n.icon className="h-4 w-4" /> {n.label}
            </NavLink>
          ))}
        </>
      )}

      {isAdmin && (
        <>
          <p className="px-4 pt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">Admin</p>
          <NavLink to="/admin/users" data-testid="nav-admin-users" className={({ isActive }) => cn('mx-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors', isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground/70 hover:bg-accent hover:text-foreground')}>
            <Users className="h-4 w-4" /> Users
          </NavLink>
          <NavLink to="/admin/transfers" data-testid="nav-admin-transfers" className={({ isActive }) => cn('mx-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors', isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground/70 hover:bg-accent hover:text-foreground')}>
            <ArrowRightLeft className="h-4 w-4" /> Transfer Requests
          </NavLink>
          <NavLink to="/admin/settings" data-testid="nav-admin-settings" className={({ isActive }) => cn('mx-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors', isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground/70 hover:bg-accent hover:text-foreground')}>
            <Shield className="h-4 w-4" /> Settings & Templates
          </NavLink>
        </>
      )}
      {!isAdmin && (
        <>
          <p className="px-4 pt-4 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">My requests</p>
          <NavLink to="/admin/transfers" data-testid="nav-my-transfers" className={({ isActive }) => cn('mx-2 px-3 py-2 rounded-lg text-sm flex items-center gap-2 transition-colors', isActive ? 'bg-primary/10 text-primary font-medium' : 'text-foreground/70 hover:bg-accent hover:text-foreground')}>
            <ArrowRightLeft className="h-4 w-4" /> Transfer Requests
          </NavLink>
        </>
      )}
    </div>
  );

  return (
    <div className="App min-h-screen bg-background">
      {/* Sidebar (desktop) */}
      <aside className="hidden md:flex md:flex-col fixed inset-y-0 left-0 w-64 border-r bg-card">
        <div className="px-4 py-4 border-b flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-display font-semibold">H</div>
          <div>
            <p className="font-display font-semibold">HR Compass</p>
            <p className="text-[10px] text-muted-foreground">Consultancy CRM</p>
          </div>
        </div>
        <div className="flex-1 overflow-y-auto py-2"><NavList /></div>
        <div className="p-3 border-t">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="w-full flex items-center gap-2 px-2 py-2 rounded-lg hover:bg-accent transition-colors" data-testid="user-menu-trigger">
                <div className="h-8 w-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium">
                  {(user?.name || 'U')[0]}
                </div>
                <div className="min-w-0 flex-1 text-left">
                  <p className="text-sm font-medium truncate">{user?.name}</p>
                  <p className="text-xs text-muted-foreground truncate capitalize">{user?.role}</p>
                </div>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" side="top" className="w-56">
              <DropdownMenuLabel>Signed in as</DropdownMenuLabel>
              <DropdownMenuLabel className="font-normal text-xs text-muted-foreground -mt-2">{user?.email}</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="user-profile"><User className="h-4 w-4 mr-2" />Profile & password</DropdownMenuItem>
              <DropdownMenuItem onClick={logout} data-testid="user-logout"><LogOut className="h-4 w-4 mr-2" />Sign out</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden sticky top-0 z-30 flex items-center justify-between px-3 h-14 border-b bg-card">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" data-testid="mobile-nav-toggle"><Menu className="h-5 w-5" /></Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <div className="px-4 py-4 border-b flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-primary text-primary-foreground flex items-center justify-center font-display font-semibold">H</div>
              <p className="font-display font-semibold">HR Compass</p>
            </div>
            <div onClick={() => setMobileOpen(false)}>
              <NavList />
            </div>
          </SheetContent>
        </Sheet>
        <div className="font-display font-semibold">HR Compass</div>
        <Button variant="ghost" size="icon" onClick={logout} data-testid="mobile-logout"><LogOut className="h-5 w-5" /></Button>
      </div>

      <main className="md:pl-64">
        {children}
      </main>
    </div>
  );
}
